

import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Getter, Mutation, State } from "vuex-class"

import CaseFilters from "@/components/CaseList/CaseFilters.vue"
import NavHeader from "@/components/NavHeader.vue"
import SlideMenu from "@/components/SlideMenu.vue"
import DisplayMessage from "./DisplayMessage.vue"

import { CurrentUser } from "@/types/user"

import { ViewsService } from "@/services/viewsService"
import ZendeskHelp from "./ZendeskHelp.vue"
import ConnectionStatus from "@/types/connectionStatus"
import QuickCreateMenu from "@/components/CaseDetails/QuickCreateMenu.vue"
import { DataSource } from "@/types"

const caseStore = namespace("cases")
const setup = namespace("setup")

@Component({
	components: {
		"nav-header": NavHeader,
		"slide-menu": SlideMenu,
		"case-filters": CaseFilters,
		"zendeskHelp": ZendeskHelp,
		"display-message": DisplayMessage,
		"quick-create-menu": QuickCreateMenu
	}
})
export default class Page extends Vue {
	$refs!: {
		slideMenu: SlideMenu
	}

	@Prop(String)
	title!: string;

	@Getter("getMe")
	public me!: CurrentUser;

	@Getter("getHasIntegrations") hasIntegrations!: boolean

	@Getter("getScreenWidth")
	public screenWidth!: number;

	@caseStore.State viewsService!: ViewsService;

	@Getter("getCaseName") getCaseName!: string;
	@Getter("getRecordName") getRecordName!: string;

	@caseStore.State
	private caseToast!: string | null

	@caseStore.Mutation
	private setCaseToast!: (message: string | null) => void

	@State
	private userToast!: string | null

	@State public notificationState!: ConnectionStatus | null

	@Mutation
	private setUserToast!: (message: string | null) => void

	@setup.Action
	private loadDataSources!: () => Promise<void>

	@setup.State
	private dataSources!: DataSource[];

	get showError (): boolean {
		return !!this.notificationState && !this.notificationState.connected
	}

	get errorIcon (): string | null {
		if (!this.notificationState || this.notificationState.connected) return null

		if (this.notificationState.ableToReconnect) return "fas fa-spinner fa-spin"

		return "fas fa-wifi-slash"
	}

	@Watch("caseToast")
	onCaseToastChanged (newMessage: string | null) {
		if (newMessage) {
			this.$bvToast.toast(newMessage, {
				title: this.getRecordName,
				variant: "danger",
				solid: true,
				autoHideDelay: 10000
			})

			this.setCaseToast(null)
		}
	}

	@Watch("userToast")
	onUserToastChanged (newMessage: string | null) {
		if (newMessage) {
			this.$bvToast.toast(newMessage, {
				title: "Message",
				variant: "danger",
				solid: true,
				autoHideDelay: 5000
			})

			this.setUserToast(null)
		}
	}

	public async mounted () {
		if (this.screenWidth <= 800) {
			this.viewsService.loadViews()
		}
		this.onCaseToastChanged(this.caseToast)

		if (!this.dataSources) {
			await this.loadDataSources()
		}
	}

	/** Displays list of routes user can navigate to */
	public get displayRoutes (): any[] {
		return (this.$router as any).options.routes
			.filter((route: any) => {
				return route && route.meta && route.meta.icon
			})
			.map((route: any) => {
				return {
					name: route.name,
					icon: route.meta.icon
				}
			})
	}

	public get currentRouteName () {
		return this.$router.currentRoute.name
	}

	public get activeViewId (): string | null {
		if (this.$router.currentRoute.name === "overview") {
			return this.viewsService.activeViewId
		}
		return null
	}

	public get viewsSummary () {
		if (!this.viewsService.viewSummary) return null
		return this.viewsService.viewSummary.summary
	}

	public gotoPage () {
		this.$router.push({ name: "overview" })
	}

	private openMenu (): void {
		this.$refs.slideMenu.open()
	}

	public async changeView (viewId: string | null) {
		this.viewsService.activeViewId = viewId
		if (this.$router.currentRoute.name === "overview") {
			this.viewsService.loadViews()
			this.$refs.slideMenu.close()
		} else {
			this.$router.push({ path: "/cases" })
		}
	}

	private refreshConnection () {
		window.location.reload()
	}

	private canAccessArea (area: string): boolean {
		if (!!this.me && !!this.me.isAdmin) {
			return true
		}

		switch (area.toLowerCase()) {
			case "settings":
				return this.canAccessSettings
		}

		return false
	}

	private get canAccessSettings (): boolean {
		if (!this.me) {
			return false
		}
		if (this.me.isAdmin || this.me.editData) {
			return true
		} else if (this.me.teams) {
			const teams = this.me.teams
			const teamIds: string[] = teams.map(a => a.id)

			if (teamIds.length < 1) {
				return false
			}

			if (!!this.dataSources && this.dataSources.length > 0) {
				const availableDataSources = this.dataSources.filter(ds => !!ds.editPermissions && ds.editPermissions.some((ep: string) => teamIds.includes(ep)))
				return !!availableDataSources && availableDataSources.length > 0
			}
		}

		return false
	}
}

